import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CheckCircle, Settings as SettingsIcon, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import AuthButton from "../../components/AuthButton";
import DarkModeToggle from "../../components/DarkModeToggle";
import { useApp } from "../../context/AppContext";
import { useTrackPageView } from "../../hooks/useTrackPageView";

function Settings() {
  useTrackPageView("Settings");
  const navigate = useNavigate();
  const { preferences, setPreferences } = useApp();
  const [cleared, setCleared] = useState(false);

  const handleClearStorage = () => {
    localStorage.clear();
    setCleared(true);

    // Reset after 2 seconds and navigate to home
    setTimeout(() => {
      navigate("/v1/");
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Settings and Dark mode toggle - top left */}
      <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
        <Button variant="ghost" size="icon" disabled className="h-9 w-9 text-muted-foreground">
          <SettingsIcon className="h-4 w-4" />
          <span className="sr-only">Settings (current page)</span>
        </Button>
        <div className="h-5 w-px bg-border" />
        <DarkModeToggle />
      </div>

      {/* Navigation - top right */}
      <nav className="absolute top-0 right-0 p-6 flex gap-2">
        <Button variant="outline" asChild>
          <Link to="/v1/">Home</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/v1/reports">Reports</Link>
        </Button>
      </nav>

      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Settings</CardTitle>
            <CardDescription>Manage your application settings and data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="pt-4 border-t space-y-6">
              {/* Authentication Section */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-2">Authentication</h3>
                <div className="space-y-3">
                  <div className="pt-2">
                    <AuthButton />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Theme</h3>
                <p className="text-sm text-muted-foreground">Toggle between light and dark mode</p>
                <div className="flex items-center gap-2">
                  <DarkModeToggle />
                  <span className="text-sm text-muted-foreground">Click to toggle theme</span>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Sample Prompts</h3>
                <p className="text-sm text-muted-foreground">
                  Show sample prompts on the landing page even when you have report history
                </p>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="sample-prompts"
                    checked={preferences.showSamplePrompts}
                    onCheckedChange={(checked) => setPreferences({ showSamplePrompts: checked })}
                  />
                  <Label htmlFor="sample-prompts" className="text-sm">
                    Always show sample prompts
                  </Label>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Clear Local Data</h3>
                <p className="text-sm text-muted-foreground">
                  This will clear all locally stored data including report history, ID mappings, and
                  preferences. Use this to reset the demo experience.
                </p>
                <Button
                  variant="destructive"
                  onClick={handleClearStorage}
                  disabled={cleared}
                  className="w-full"
                >
                  {cleared ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Data Cleared - Redirecting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear All Local Storage
                    </>
                  )}
                </Button>
              </div>

              {cleared && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    All local data has been cleared successfully. Redirecting to home page...
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Settings;
